import { type PatientInfoSection } from "cindra/CindraPatientInfoPanel.tsx";

// Debug logging flag
const ENABLE_PARSING_LOGS = false;

/**
 * Parse markdown content into sections with label/value pairs
 * Handles H2, H3, H4 headers as section titles and extracts key-value pairs
 */
export function parsePatientInfoMarkdown(markdown: string): PatientInfoSection[] {
    const sections: PatientInfoSection[] = [];
    const lines = markdown.split('\n');

    let currentSection: PatientInfoSection | null = null;

    for (const line of lines) {
        const trimmedLine = line.trim();

        // Skip empty lines
        if (!trimmedLine) continue;

        // Skip H3 headers that contain "Patient Overview" - these are duplicates
        if (trimmedLine.match(/^###\s+.*[Pp]atient\s+[Oo]verview/)) {
            if (ENABLE_PARSING_LOGS) console.log('[parsePatientInfoMarkdown] Skipping H3 patient overview header:', trimmedLine);
            continue;
        }

        // Check for H2 section header (## Title)
        if (trimmedLine.startsWith('## ')) {
            // Save previous section if exists
            if (currentSection && currentSection.items.length > 0) {
                sections.push(currentSection);
            }
            // Start new section
            const title = trimmedLine.replace(/^##\s*/, '').replace(/\*\*/g, '').trim();
            if (ENABLE_PARSING_LOGS) console.log('[parsePatientInfoMarkdown] Found H2 section:', title);
            currentSection = {
                title,
                items: []
            };
        }
        // Check for H3 subsection header (### Title) - treat as section too
        else if (trimmedLine.startsWith('### ')) {
            // Save previous section if exists
            if (currentSection && currentSection.items.length > 0) {
                sections.push(currentSection);
            }
            // Start new section
            const title = trimmedLine.replace(/^###\s*/, '').replace(/\*\*/g, '').trim();
            if (ENABLE_PARSING_LOGS) console.log('[parsePatientInfoMarkdown] Found H3 section:', title);
            currentSection = {
                title,
                items: []
            };
        }
        // Check for H4 section header (#### Title) - treat as section too
        else if (trimmedLine.startsWith('#### ')) {
            // Save previous section if exists
            if (currentSection && currentSection.items.length > 0) {
                sections.push(currentSection);
            }
            // Start new section
            const title = trimmedLine.replace(/^####\s*/, '').replace(/\*\*/g, '').trim();
            if (ENABLE_PARSING_LOGS) console.log('[parsePatientInfoMarkdown] Found H4 section:', title);
            currentSection = {
                title,
                items: []
            };
        }
        // Check for list item or line with colon (label: value)
        else if (trimmedLine.startsWith('-') || trimmedLine.startsWith('*') || trimmedLine.includes(':')) {
            let content = trimmedLine;

            // Check if this is an indented sub-item (nested list)
            const isIndented = line.startsWith('  ') || line.startsWith('\t');

            // Remove list marker if present
            if (content.startsWith('-') || content.startsWith('*')) {
                content = content.replace(/^[-*]\s*/, '').trim();
            }

            // Remove bold markers
            content = content.replace(/\*\*/g, '');

            // If this is an indented item and we have a current section with items
            // Append it to the last item's value as a nested list item
            if (isIndented && currentSection && currentSection.items.length > 0) {
                const lastItem = currentSection.items[currentSection.items.length - 1];
                // Append as a new line to create a multi-line value
                if (lastItem.value) {
                    lastItem.value += '\n' + content;
                } else {
                    // Mark this as a nested list item with a special prefix
                    // This helps the renderer distinguish it from regular values
                    lastItem.value = '•LIST_ITEM•' + content;
                }
            }
            // Parse label:value
            else if (content.includes(':')) {
                const colonIndex = content.indexOf(':');
                const label = content.substring(0, colonIndex).trim();
                const value = content.substring(colonIndex + 1).trim();

                if (label && label.length < 100) {
                    if (!currentSection) {
                        // Create default section if none exists
                        if (ENABLE_PARSING_LOGS) console.log('[parsePatientInfoMarkdown] Creating default section for orphaned content');
                        currentSection = { title: '', items: [] };
                    }
                    // Allow empty values for labels that will have nested items
                    currentSection.items.push({ label, value: value || '' });
                }
            }
        }
    }

    // Add final section
    if (currentSection && currentSection.items.length > 0) {
        sections.push(currentSection);
    }

    if (ENABLE_PARSING_LOGS) console.log('[parsePatientInfoMarkdown] Parsed sections:', sections);
    return sections;
}
